import { useState } from "react";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";

interface PricingCardProps {
  title: string;
  monthlyPrice: any;
  yearlyPrice: any;
  isYearly: boolean;
  users: string;
  description: string;
  features: string[];
  isHighlighted?: boolean;
  onSubscribe: () => void;
  loading?: boolean;

  // New Props
  deploymentMainOptions: Array<{ id: string; name: string; price: number }>;
  deploymentFixedAddOns: Array<{ id: string; name: string; price: number }>;
  selectedDeploymentId: string | null;
  onDeploymentChange: (id: string) => void;

  allIntegrations: Array<{ id: string; name: string; price: any }>;
  selectedIntegrationIds: string[];
  onRemoveIntegration: (id: string) => void;
  isTierSelected: boolean;
  integrationServices?: Array<{ name: string; cost: string }>;
}

export default function PricingCard({
  title,
  monthlyPrice,
  yearlyPrice,
  isYearly,
  users,
  description,
  features,
  isHighlighted = false,
  onSubscribe,
  loading = false,
  deploymentMainOptions,
  deploymentFixedAddOns,
  selectedDeploymentId,
  onDeploymentChange,
  allIntegrations,
  selectedIntegrationIds,
  onRemoveIntegration,
  isTierSelected,
  integrationServices,
}: PricingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const planPrice = isYearly ? yearlyPrice : monthlyPrice;

  // Format price with currency (convert from kobo to naira)
  const formatCost = (price: number) => {
    // Convert kobo to naira (1 naira = 100 kobo)
    const priceInNaira = price;

    return new Intl.NumberFormat("en-NG", {
      style: "decimal",
      // currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(priceInNaira);
  };
  // const formatCost = (cost: number) => {
  //   return new Intl.NumberFormat("en-NG", {
  //     style: "decimal",
  //     // currency: "NGN",
  //     minimumFractionDigits: 0,
  //   }).format(cost);
  // };

  // Calculate Deployment Cost
  const selectedDeploymentOption = deploymentMainOptions.find(
    (d) => d.id === selectedDeploymentId,
  );
  const deploymentTotal = selectedDeploymentOption
    ? selectedDeploymentOption.price +
      deploymentFixedAddOns.reduce((sum, item) => sum + item.price, 0)
    : 0;

  // Calculate Integrations Cost
  const selectedIntegrations = allIntegrations.filter((i) =>
    selectedIntegrationIds.includes(i.id),
  );
  const integrationsTotal = selectedIntegrations.reduce(
    (sum, item) => sum + item.price,
    0,
  );

  const totalPayable = planPrice + deploymentTotal + integrationsTotal;

  return (
    <div
      className={`relative rounded-[20px] p-8 transition-all duration-300 hover:scale-105 font-abel flex flex-col h-full ${
        isHighlighted
          ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-2xl ring-4 ring-blue-200"
          : "bg-white shadow-lg hover:shadow-xl border border-gray-200"
      }`}
    >
      {isHighlighted && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-amber-400 text-gray-900 px-4 py-1 rounded-full text-sm font-medium">
          Most Popular
        </div>
      )}

      <div className="mb-6">
        <h3
          className={`text-2xl font-medium mb-2 ${
            isHighlighted ? "text-white" : "text-gray-900"
          }`}
        >
          {title}
        </h3>
        <p
          className={`text-sm ${
            isHighlighted ? "text-blue-100" : "text-gray-600"
          }`}
        >
          {description}
        </p>
      </div>

      <div className="mb-0">
        <div className="flex items-baseline">
          <span
            className={`text-2xl font-normal ${
              isHighlighted ? "text-white" : "text-gray-900"
            }`}
          >
            <span className="text-[14px] font-normal align-super pt-1">N</span>
            {formatCost(planPrice)}
          </span>
          <span
            className={`text-[12px] ${
              isHighlighted ? "text-blue-100" : "text-gray-600"
            }`}
          >
            /{isYearly ? "year" : "month"}
          </span>
        </div>
      </div>

      <div
        className={`mb-6 pb-6 border-b ${
          isHighlighted ? "border-blue-500" : "border-gray-200"
        }`}
      >
        <p
          className={`font-medium text-sm ${
            isHighlighted ? "text-white" : "text-gray-900"
          }`}
        >
          {users}
        </p>
      </div>

      {!isTierSelected && (
        <ul className="space-y-3 mb-8">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  isHighlighted ? "text-blue-200" : "text-blue-600"
                }`}
              />
              <span
                className={`text-sm ${
                  isHighlighted ? "text-white" : "text-gray-700"
                }`}
              >
                {feature}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Deployment Section */}
      <div className="mb-6 space-y-3">
        {/* <label
          className={`block text-sm font-medium ${
            isHighlighted ? "text-white" : "text-gray-700"
          }`}
        >
          Choose Deployment Type
        </label> */}
        <select
          value={selectedDeploymentId || ""}
          onChange={(e) => onDeploymentChange(e.target.value)}
          className={`w-full p-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 outline-none ${
            isHighlighted
              ? "bg-white/10 border-blue-400 text-white option:text-gray-900"
              : "bg-white border-gray-300 text-gray-900"
          }`}
        >
          <option value="" className="text-gray-500">
            Select Deployment...
          </option>
          {deploymentMainOptions.map((opt) => (
            <option key={opt.id} value={opt.id} className="text-gray-900">
              {opt.name} ({formatCost(opt.price)})
            </option>
          ))}
        </select>

        {/* Auto-added Deployment Items */}
        {selectedDeploymentId && (
          <div
            className={`mt-3 p-3 rounded-lg text-sm space-y-2 ${
              isHighlighted ? "bg-white/10" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center pb-2 border-b border-gray-200/20">
              <span
                className={isHighlighted ? "text-blue-100" : "text-gray-600"}
              >
                {selectedDeploymentOption?.name}
              </span>
              <span className="font-medium">
                {selectedDeploymentOption &&
                  formatCost(selectedDeploymentOption.price)}
              </span>
            </div>
            {deploymentFixedAddOns.map((addon) => (
              <div key={addon.id} className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-500" />
                  <span
                    className={
                      isHighlighted ? "text-blue-100" : "text-gray-600"
                    }
                  >
                    {addon.name}
                  </span>
                </div>
                <span className="font-medium">{formatCost(addon.price)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Integrations - Hidden from card as per requirements */}
      {/* {selectedIntegrations.length > 0 && (
        <div className="mb-6 pt-4 border-t border-gray-200/50">
          <h4
            className={`text-sm font-medium mb-3 ${
              isHighlighted ? "text-white" : "text-gray-700"
            }`}
          >
            Selected Integrations
          </h4>
          <div className="space-y-2">
            {selectedIntegrations.map((int) => (
              <div
                key={int.id}
                className={`flex justify-between items-center text-sm p-2 rounded ${
                  isHighlighted ? "bg-white/20" : "bg-blue-50"
                }`}
              >
                <span
                  className={isHighlighted ? "text-white" : "text-gray-700"}
                >
                  {int.name}
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{formatCost(int.price)}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveIntegration(int.id);
                    }}
                    className="p-1 hover:bg-red-100 rounded-full text-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )} */}

      {/* Total Payable - Moved to floating sidebar */}
      {selectedDeploymentId && (
        <div
          className={`mt-0 pt-3 border-t ${
            isHighlighted ? "border-blue-400" : "border-gray-200"
          }`}
        >
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`w-full py-2.5 px-3 mt-4 border rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              isHighlighted
                ? "border-white/30 text-white hover:bg-white/10"
                : "border-gray-100 text-gray-500 hover:bg-gray-50"
            }`}
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span className="text-sm">
              {isExpanded ? "Hide Details" : "View Details"}
            </span>
          </button>

          {isExpanded &&
            integrationServices &&
            integrationServices.length > 0 && (
              <div
                className={`mt-4 pt-4 border-t ${
                  isHighlighted ? "border-white/20" : "border-gray-100"
                }`}
              >
                <div className="flex justify-between mb-3 text-sm font-medium">
                  <span
                    className={isHighlighted ? "text-white" : "text-gray-900"}
                  >
                    Integration Services
                  </span>
                  <span
                    className={isHighlighted ? "text-white" : "text-gray-900"}
                  >
                    Cost (NGN)
                  </span>
                </div>
                <ul className="space-y-2">
                  {integrationServices.map((service, i) => (
                    <li key={i} className="flex justify-between text-xs">
                      <span
                        className={
                          isHighlighted ? "text-blue-100" : "text-gray-600"
                        }
                      >
                        {service.name}
                      </span>
                      <span
                        className={
                          isHighlighted ? "text-white" : "text-gray-900"
                        }
                      >
                        {service.cost}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      )}

      <button
        onClick={onSubscribe}
        disabled={loading}
        className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 flex justify-center items-center mt-auto ${
          isHighlighted
            ? "bg-white text-blue-600 hover:bg-blue-50"
            : "bg-blue-600 text-white hover:bg-blue-700"
        } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {loading ? "Processing..." : "Get Started"}
      </button>
    </div>
  );
}
